import { AuthService } from '../auth-service';
import { Message, SocketState } from './types';

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 3;
  private readonly reconnectDelay = 1000;
  private baseUrl: string;
  private intentionalClose = false;

  public onMessage?: (message: Message) => void;
  public onError?: (error: Error) => void;
  public onMaxRetriesExceeded?: () => void;
  public onStateChange?: (state: SocketState) => void;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  public getReadyState(): number | null {
    return this.ws?.readyState ?? null;
  }

  public async connect(): Promise<void> {
    // Reset intentional close flag on new connection attempt
    this.intentionalClose = false;

    try {
      const token = await AuthService.getAuthToken();
      const wsUrlWithAuth = `${this.baseUrl}${this.baseUrl.includes('?') ? '&' : '?'}token=${token}`;
      
      this.ws = new WebSocket(wsUrlWithAuth);
      
      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.onStateChange?.(SocketState.CONNECTED);
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as Message;
          this.onMessage?.(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocketManager] onclose event fired. Event:', event, 'intentionalClose:', this.intentionalClose, 'readyState:', this.ws ? this.ws.readyState : 'null');
        this.onStateChange?.(SocketState.CLOSED);
        
        // Check if the close was due to auth error (code 4001)
        if (event.code === 401) {
          console.log('[WebSocketManager] onclose: Auth error detected with code 401.');
          AuthService.handleAuthError(new Error('WebSocket authentication failed'));
          this.ws = null;
          return;
        }

        // Don't reconnect if it was an intentional closure or normal closure
        if (this.intentionalClose || event.code === 1000) {
          console.log('[WebSocketManager] onclose: Intentional or normal closure detected (code:', event.code, '). Not attempting reconnection.');
          this.ws = null;
          return;
        }

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          this.onStateChange?.(SocketState.RECONNECTING);
          console.log('[WebSocketManager] onclose: Attempting reconnection. Attempt:', this.reconnectAttempts);
          setTimeout(() => {
            // Don't reconnect if we've been intentionally closed while waiting
            if (!this.intentionalClose) {
              this.connect();
            } else {
              console.log('[WebSocketManager] onclose: Skipping reconnection because intentionalClose is true.');
            }
          }, this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1));
        } else {
          console.error('[WebSocketManager] onclose: Failed to establish WebSocket connection after max retries');
          this.onMaxRetriesExceeded?.();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocketManager] onerror event. Error:', error, 'intentionalClose:', this.intentionalClose, 'readyState:', this.ws ? this.ws.readyState : 'null');
        this.onError?.(new Error('WebSocket connection error'));
        // Only close if not already closing/closed
        if (this.ws && this.ws.readyState < 2) {
          console.log('[WebSocketManager] onerror: Closing websocket as readyState is less than 2.');
          this.ws.close();
        } else {
          console.log('[WebSocketManager] onerror: WebSocket already closing or closed.');
        }
      };

    } catch (error) {
      if (AuthService.isAuthError(error)) {
        AuthService.handleAuthError(error);
        return;
      }
      console.error('Failed to establish WebSocket connection:', error);
      this.onError?.(error instanceof Error ? error : new Error('Failed to establish WebSocket connection'));
      this.onStateChange?.(SocketState.ERROR);
    }
  }

  public disconnect(): void {
    console.log('[WebSocketManager] disconnect() called', {
      hasWs: !!this.ws,
      readyState: this.ws?.readyState,
      intentionalClose: this.intentionalClose,
      timestamp: new Date().toISOString()
    });
    if (this.ws) {
      console.log('[WebSocketManager] disconnect() called. readyState:', this.ws.readyState, 'intentionalClose before setting:', this.intentionalClose);
      // Mark this as an intentional close
      this.intentionalClose = true;
      // Always use code 1000 for normal closure when explicitly disconnecting
      this.ws.close(1000, 'Normal closure');
      // Reset reconnection attempts since this was intentional
      this.reconnectAttempts = 0;
    }
  }

  public setUrl(newUrl: string): void {
    if (this.baseUrl === newUrl) {
      return;
    }
    this.baseUrl = newUrl;
    console.log('[WebSocketManager] baseUrl updated to', newUrl);
    if (this.ws) {
      console.log('[WebSocketManager] Disconnecting active connection due to URL change');
      this.disconnect();
      // Optionally, if you want to auto-reconnect immediately, uncomment the line below
      // this.connect();
    }
  }
}

export default WebSocketManager; 